/**
 * Customer API Service
 * Service layer for customer-related API calls
 *
 * B2.6'-fe-seam: `list`/`getById` gain a live-module leg (the `masters` key,
 * client.ts) executing the B0.7' contract operations against `/graphql`
 * (masters.operations.ts → masters.gql-map.ts). Signatures unchanged.
 *
 * CU-1a masters flip: `checkExists` joins the live leg (the one-query
 * companyByTaxId dedupe — COMPANY_FOUND pre-fill included) and the list leg
 * plumbs search/categoryIds/isActive filter variables. Creates/updates and
 * the location/contact/payment collection legs stay on the mock seam
 * pending the B2.7' completion pass (register-listed residual).
 */

import apiClient from './axios.config';
import { isLiveModule } from './client';
import { refuseUnobservableMastersWrite } from './mastersWriteReach';
import {
  GraphQLTransportError,
  gqlMutate,
  gqlRequest,
  throwOnUserErrors,
} from './graphql.client';
import {
  mastersOperations,
  type CompanyByTaxIdCheckResult,
  type CustomerContactSetActiveResult,
  type CustomerDetailPageResult,
  type CustomerListPageResult,
  type CustomerLocationSetActiveResult,
  type CustomerSetActiveResult,
  type MastersDetailVariables,
  type MastersListVariables,
} from './masters.operations';
import {
  CUSTOMER_LIST_PLUMBED_KEYS,
  listConstraintsDroppedOnLive,
  mapCustomerCheckExists,
  mapCustomerDetailNode,
  mapCustomerListNode,
  toCustomerFilterInput,
  toFirstVariable,
  toLegacyPagination,
} from './masters.gql-map';
import { API_ROUTES, CUSTOMER_ROUTES } from '@/constants/api.constants';
import type {
  CustomerListResponse,
  CustomerDetailResponse,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerCheckExistsResponse,
  CustomerCheckExistsParams,
  QuickCreateCustomerRequest,
  QuickCreateCustomerResponse,
  ToggleCustomerStatusRequest,
  ToggleCustomerStatusResponse,
  CustomerListParams,
  CustomerLocationsResponse,
  CustomerLocationResponse,
  CustomerLocationListParams,
  CreateCustomerLocationRequest,
  UpdateCustomerLocationRequest,
  ToggleCustomerLocationStatusResponse,
  SetDefaultCustomerLocationResponse,
  CustomerContactResponse,
  CustomerContactDetailResponse,
  CreateCustomerContactRequest,
  UpdateCustomerContactRequest,
  ToggleCustomerContactStatusResponse,
  SetPrimaryCustomerContactResponse,
  CustomerContactListParams,
  // Payment types
  CustomerPaymentResponse,
  CreateCustomerPaymentRequest,
  UpdateCustomerPaymentRequest,
  CustomerPaymentMutationResponse,
} from '@/types/api/customer';
import type { ListResponse } from '@/types/api/common';

/**
 * Customer API service object
 */
export const customerApi = {
  // ============================================================================
  // CUSTOMER CRUD
  // ============================================================================

  /**
   * GET /api/customers
   * Fetch list of customers with optional filtering and pagination
   */
  list: async (params?: CustomerListParams): Promise<CustomerListResponse> => {
    if (isLiveModule('masters')) {
      // CU-1a: search/categoryIds/isActive plumb into the SDL filter; the
      // legacy country facet has no SDL twin (census-dropped), sort + cursor
      // plumbing remains for the B2.7' completion pass.
      const dropped = listConstraintsDroppedOnLive(params, CUSTOMER_LIST_PLUMBED_KEYS);
      if (dropped.length > 0) {
        console.warn(
          `[customerApi.list] live leg drops [${dropped.join(', ')}] until the B2.7' completion pass plumbs sort/cursor variables`
        );
      }
      const result = await gqlRequest<CustomerListPageResult, MastersListVariables>(
        mastersOperations.customerList,
        {
          first: toFirstVariable(params?.pageSize),
          after: null,
          filter: toCustomerFilterInput(params) ?? null,
        }
      );
      return {
        data: result.customers.edges.map((edge) => mapCustomerListNode(edge.node)),
        pagination: toLegacyPagination(result.customers.totalCount, params?.pageSize),
      };
    }
    const response = await apiClient.get<CustomerListResponse>(API_ROUTES.CUSTOMERS, {
      params,
    });
    return response.data;
  },

  /**
   * GET /api/customers/:customerId
   * Fetch single customer details by ID
   */
  getById: async (customerId: number): Promise<CustomerDetailResponse> => {
    if (isLiveModule('masters')) {
      // Id posture: numeric legacy id as-is; opaque-global-id divergence
      // settles at the masters freeze (masters.gql-map.ts header).
      const result = await gqlRequest<CustomerDetailPageResult, MastersDetailVariables>(
        mastersOperations.customerDetail,
        { id: String(customerId) }
      );
      if (!result.customer) {
        throw new GraphQLTransportError({
          message: `Customer ${customerId} not found`,
          graphQLErrors: ['NOT_FOUND'],
          status: 404,
        });
      }
      return mapCustomerDetailNode(result.customer);
    }
    const response = await apiClient.get<CustomerDetailResponse>(
      `${API_ROUTES.CUSTOMERS}/${customerId}`
    );
    return response.data;
  },

  /**
   * POST /api/customers
   * Create a new customer
   */
  create: async (data: CreateCustomerRequest): Promise<CustomerDetailResponse> => {
    refuseUnobservableMastersWrite('customerApi.create');
    const response = await apiClient.post<CustomerDetailResponse>(
      API_ROUTES.CUSTOMERS,
      data
    );
    return response.data;
  },

  /**
   * PUT /api/customers/:customerId
   * Update existing customer
   */
  update: async (
    customerId: number,
    data: UpdateCustomerRequest
  ): Promise<CustomerDetailResponse> => {
    refuseUnobservableMastersWrite('customerApi.update');
    const response = await apiClient.put<CustomerDetailResponse>(
      `${API_ROUTES.CUSTOMERS}/${customerId}`,
      data
    );
    return response.data;
  },

  /**
   * PATCH /api/customers/:customerId/toggle
   * Toggle customer active status
   *
   * LIVE (CU-1b masters activation) — `customerSetActive`, the boolean
   * travelling. `CustomerUpdateInput` declares no `isActive`, exactly like
   * its vendor and product siblings, so `update` may never carry an
   * activation and stays a refusal.
   *
   * Unlike the vendor twin the echo is LEAN: the mock answers
   * {message, customerId, isActive} and the declared response agrees, so the
   * facade projects down rather than inventing a detail row for the wire.
   */
  toggleStatus: async (
    customerId: number,
    isActive: boolean
  ): Promise<ToggleCustomerStatusResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<CustomerSetActiveResult>(
        mastersOperations.customerSetActive,
        { input: { customerId: String(customerId), isActive } }
      );
      throwOnUserErrors(result.customerSetActive.userErrors, 'customerSetActive');
      const customer = result.customerSetActive.customer;
      if (!customer) {
        throw new GraphQLTransportError({
          message: 'customerSetActive returned neither a customer nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      return {
        message: 'Status updated',
        customerId: customer.customerId,
        isActive: customer.isActive,
      };
    }
    const response = await apiClient.patch<ToggleCustomerStatusResponse>(
      `${API_ROUTES.CUSTOMERS}/${customerId}/toggle`,
      { isActive } as ToggleCustomerStatusRequest
    );
    return response.data;
  },

  /**
   * GET /api/customers/check-exists
   * Check if customer exists by country and tax ID
   */
  checkExists: async (params: CustomerCheckExistsParams): Promise<CustomerCheckExistsResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlRequest<CompanyByTaxIdCheckResult>(
        mastersOperations.companyByTaxIdCheck,
        { countryId: params.countryId, localTaxId: params.localTaxId }
      );
      return mapCustomerCheckExists(result);
    }
    const response = await apiClient.get<CustomerCheckExistsResponse>(
      `${API_ROUTES.CUSTOMERS}/check-exists`,
      { params }
    );
    return response.data;
  },

  /**
   * POST /api/customers/quick-create
   * Quick create customer from existing company data (when companyExists=true)
   */
  quickCreate: async (
    data: QuickCreateCustomerRequest
  ): Promise<QuickCreateCustomerResponse> => {
    refuseUnobservableMastersWrite('customerApi.quickCreate');
    const response = await apiClient.post<QuickCreateCustomerResponse>(
      `${API_ROUTES.CUSTOMERS}/quick-create`,
      data
    );
    return response.data;
  },

  // ============================================================================
  // CUSTOMER LOCATIONS
  // ============================================================================

  /**
   * GET /api/customers/:customerId/locations
   * Fetch customer locations by customer ID
   */
  getLocations: async (
    customerId: number,
    params?: CustomerLocationListParams
  ): Promise<CustomerLocationsResponse> => {
    const response = await apiClient.get<CustomerLocationsResponse>(
      CUSTOMER_ROUTES.LOCATIONS(customerId),
      { params }
    );
    return response.data;
  },

  /**
   * GET /api/customers/:customerId/locations/:locationId
   * Fetch single customer location by ID
   */
  getLocationById: async (
    customerId: number,
    locationId: number
  ): Promise<CustomerLocationResponse> => {
    const response = await apiClient.get<CustomerLocationResponse>(
      `${CUSTOMER_ROUTES.LOCATIONS(customerId)}/${locationId}`
    );
    return response.data;
  },

  /**
   * POST /api/customers/:customerId/locations
   * Create a new location for a customer
   */
  createLocation: async (
    customerId: number,
    data: Omit<CreateCustomerLocationRequest, 'customerId'>
  ): Promise<CustomerLocationResponse> => {
    refuseUnobservableMastersWrite('customerApi.createLocation');
    const requestData: CreateCustomerLocationRequest = {
      ...data,
      customerId,
    };
    const response = await apiClient.post<CustomerLocationResponse>(
      CUSTOMER_ROUTES.LOCATIONS(customerId),
      requestData
    );
    return response.data;
  },

  /**
   * PUT /api/customers/:customerId/locations/:locationId
   * Update an existing customer location
   */
  updateLocation: async (
    customerId: number,
    locationId: number,
    data: UpdateCustomerLocationRequest
  ): Promise<CustomerLocationResponse> => {
    refuseUnobservableMastersWrite('customerApi.updateLocation');
    const response = await apiClient.put<CustomerLocationResponse>(
      `${CUSTOMER_ROUTES.LOCATIONS(customerId)}/${locationId}`,
      data
    );
    return response.data;
  },

  /**
   * PATCH /api/customers/:customerId/locations/:locationId/toggle
   * Toggle customer location active status
   */
  toggleLocationStatus: async (
    customerId: number,
    locationId: number,
    isActive: boolean
  ): Promise<ToggleCustomerLocationStatusResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<CustomerLocationSetActiveResult>(
        mastersOperations.customerLocationSetActive,
        { input: { customerId: String(customerId), locationId, isActive } }
      );
      throwOnUserErrors(
        result.customerLocationSetActive.userErrors,
        'customerLocationSetActive'
      );
      const location = result.customerLocationSetActive.location;
      if (!location) {
        throw new GraphQLTransportError({
          message: 'customerLocationSetActive returned neither a location nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      // LEAN echo — the toggle moves neither default flag, so neither is
      // echoed; `setDefaultLocation` owns those and stays a refusal.
      return {
        message: 'Status updated',
        locationId: location.locationId,
        isActive: location.isActive,
      };
    }
    const response = await apiClient.patch<ToggleCustomerLocationStatusResponse>(
      `${CUSTOMER_ROUTES.LOCATIONS(customerId)}/${locationId}/toggle`,
      { isActive }
    );
    return response.data;
  },

  /**
   * PATCH /api/customers/:customerId/locations/:locationId/set-default
   * Set customer location as default shipping and/or billing address
   * @param data - Object with isDefaultShipping and/or isDefaultBilling flags
   */
  setDefaultLocation: async (
    customerId: number,
    locationId: number,
    data: { isDefaultShipping?: boolean; isDefaultBilling?: boolean }
  ): Promise<SetDefaultCustomerLocationResponse> => {
    refuseUnobservableMastersWrite('customerApi.setDefaultLocation');
    const response = await apiClient.patch<SetDefaultCustomerLocationResponse>(
      `${CUSTOMER_ROUTES.LOCATIONS(customerId)}/${locationId}/set-default`,
      data
    );
    return response.data;
  },

  // ============================================================================
  // CUSTOMER CONTACTS
  // ============================================================================

  /**
   * GET /api/customers/:customerId/contacts
   * Fetch customer contacts by customer ID
   */
  getContacts: async (
    customerId: number,
    params?: CustomerContactListParams
  ): Promise<ListResponse<CustomerContactResponse>> => {
    const response = await apiClient.get<ListResponse<CustomerContactResponse>>(
      CUSTOMER_ROUTES.CONTACTS(customerId),
      { params }
    );
    return response.data;
  },

  /**
   * GET /api/customers/:customerId/contacts/:contactId
   * Fetch single customer contact by ID
   */
  getContactById: async (
    customerId: number,
    contactId: number
  ): Promise<CustomerContactDetailResponse> => {
    const response = await apiClient.get<CustomerContactDetailResponse>(
      `${CUSTOMER_ROUTES.CONTACTS(customerId)}/${contactId}`
    );
    return response.data;
  },

  /**
   * POST /api/customers/:customerId/contacts
   * Create a new contact for a customer
   */
  createContact: async (
    customerId: number,
    data: Omit<CreateCustomerContactRequest, 'customerId'>
  ): Promise<CustomerContactDetailResponse> => {
    refuseUnobservableMastersWrite('customerApi.createContact');
    const requestData: CreateCustomerContactRequest = {
      ...data,
      customerId,
    };
    const response = await apiClient.post<CustomerContactDetailResponse>(
      CUSTOMER_ROUTES.CONTACTS(customerId),
      requestData
    );
    return response.data;
  },

  /**
   * PUT /api/customers/:customerId/contacts/:contactId
   * Update an existing customer contact
   */
  updateContact: async (
    customerId: number,
    contactId: number,
    data: UpdateCustomerContactRequest
  ): Promise<CustomerContactDetailResponse> => {
    refuseUnobservableMastersWrite('customerApi.updateContact');
    const response = await apiClient.put<CustomerContactDetailResponse>(
      `${CUSTOMER_ROUTES.CONTACTS(customerId)}/${contactId}`,
      data
    );
    return response.data;
  },

  /**
   * PATCH /api/customers/:customerId/contacts/:contactId/toggle
   * Toggle customer contact active status
   */
  toggleContactStatus: async (
    customerId: number,
    contactId: number,
    isActive: boolean
  ): Promise<ToggleCustomerContactStatusResponse> => {
    if (isLiveModule('masters')) {
      const result = await gqlMutate<CustomerContactSetActiveResult>(
        mastersOperations.customerContactSetActive,
        { input: { customerId: String(customerId), contactId, isActive } }
      );
      throwOnUserErrors(
        result.customerContactSetActive.userErrors,
        'customerContactSetActive'
      );
      const contact = result.customerContactSetActive.contact;
      if (!contact) {
        throw new GraphQLTransportError({
          message: 'customerContactSetActive returned neither a contact nor userErrors',
          graphQLErrors: ['EMPTY_PAYLOAD'],
        });
      }
      // LEAN echo here, the FULL contact row on
      // `masterDataContactApi.toggleCustomerContactStatus` — one document
      // serving two facades whose declared responses differ, which is why
      // the operation selects the superset.
      return {
        message: 'Status updated',
        contactId: contact.contactId,
        isActive: contact.isActive,
      };
    }
    const response = await apiClient.patch<ToggleCustomerContactStatusResponse>(
      `${CUSTOMER_ROUTES.CONTACTS(customerId)}/${contactId}/toggle`,
      { isActive }
    );
    return response.data;
  },

  /**
   * PATCH /api/customers/:customerId/contacts/:contactId/set-primary
   * Set customer contact as primary
   */
  setPrimaryContact: async (
    customerId: number,
    contactId: number,
    isPrimary: boolean
  ): Promise<SetPrimaryCustomerContactResponse> => {
    refuseUnobservableMastersWrite('customerApi.setPrimaryContact');
    const response = await apiClient.patch<SetPrimaryCustomerContactResponse>(
      `${CUSTOMER_ROUTES.CONTACTS(customerId)}/${contactId}/set-primary`,
      { isPrimary }
    );
    return response.data;
  },

  // ============================================================================
  // CUSTOMER PAYMENT
  // ============================================================================

  /**
   * GET /api/customers/:customerId/payment
   * Fetch customer payment data
   */
  getPayment: async (customerId: number): Promise<CustomerPaymentResponse> => {
    const response = await apiClient.get<CustomerPaymentResponse>(
      CUSTOMER_ROUTES.PAYMENT(customerId)
    );
    return response.data;
  },

  /**
   * POST /api/customers/:customerId/payment
   * Create customer payment data
   */
  createPayment: async (
    customerId: number,
    data: CreateCustomerPaymentRequest
  ): Promise<CustomerPaymentMutationResponse> => {
    refuseUnobservableMastersWrite('customerApi.createPayment');
    const response = await apiClient.post<CustomerPaymentMutationResponse>(
      CUSTOMER_ROUTES.PAYMENT(customerId),
      data
    );
    return response.data;
  },

  /**
   * PUT /api/customers/:customerId/payment
   * Update customer payment data
   */
  updatePayment: async (
    customerId: number,
    data: UpdateCustomerPaymentRequest
  ): Promise<CustomerPaymentMutationResponse> => {
    refuseUnobservableMastersWrite('customerApi.updatePayment');
    const response = await apiClient.put<CustomerPaymentMutationResponse>(
      CUSTOMER_ROUTES.PAYMENT(customerId),
      data
    );
    return response.data;
  },
};

export default customerApi;
